/**
 * useByokKeys
 *
 * Shared hook exposing the set of BYOK providers the current user has an active
 * key for. Backed by a module-level cache + subscriber list so that many node
 * instances mounting at once trigger only a single network fetch.
 *
 * Used by per-node 🔑/💳 badge logic (NodeAdapters) and the API Keys settings
 * page (to refresh the shared set after add/delete).
 */

import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { byokService } from '../services/ByokService';

// Module-level shared state
let cachedProviders: Set<string> | null = null;
let inflight: Promise<Set<string>> | null = null;
let lastUserId: string | null = null;
const subscribers = new Set<(providers: Set<string>) => void>();

function notify(providers: Set<string>): void {
  cachedProviders = providers;
  subscribers.forEach(fn => fn(providers));
}

async function fetchProviders(userId: string): Promise<Set<string>> {
  if (inflight && lastUserId === userId) return inflight;
  lastUserId = userId;
  inflight = (async () => {
    try {
      const creds = await byokService.listCredentials();
      const set = new Set(creds.map(c => c.provider));
      notify(set);
      return set;
    } catch {
      const empty = new Set<string>();
      notify(empty);
      return empty;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/** Invalidate the shared cache and refetch (call after add/delete). */
export function refreshByokKeys(): void {
  cachedProviders = null;
  inflight = null;
  if (lastUserId) fetchProviders(lastUserId);
}

export interface UseByokKeysResult {
  /** Providers (e.g. 'openai','falai') the user has an active key for. */
  providers: Set<string>;
  /** True once the initial fetch has resolved. */
  loaded: boolean;
  /** Force a refetch (and broadcast to all subscribers). */
  refresh: () => void;
}

export function useByokKeys(): UseByokKeysResult {
  const { user } = useAuth();
  const [providers, setProviders] = useState<Set<string>>(cachedProviders ?? new Set());
  const [loaded, setLoaded] = useState<boolean>(cachedProviders != null);

  useEffect(() => {
    const handler = (next: Set<string>) => {
      setProviders(next);
      setLoaded(true);
    };
    subscribers.add(handler);

    if (!user?.id) {
      // Logged out — clear the shared set.
      cachedProviders = new Set();
      setProviders(cachedProviders);
      setLoaded(true);
    } else if (cachedProviders != null && lastUserId === user.id) {
      setProviders(cachedProviders);
      setLoaded(true);
    } else {
      fetchProviders(user.id);
    }

    return () => {
      subscribers.delete(handler);
    };
  }, [user?.id]);

  const refresh = useCallback(() => {
    refreshByokKeys();
  }, []);

  return { providers, loaded, refresh };
}

export default useByokKeys;
